/**
 * CotPaymentsModal — Panel de Cobros de la cotización
 *
 * Muestra al especialista:
 *   • El link público para que el cliente pague (copiable + botón enviar email).
 *   • Resumen de saldo (Total / Pagado / Pendiente).
 *   • Historial de pagos con status, PDF recibo, y opción de refund (admin).
 *
 * Requiere `cot.public_token` (o sea, cotización publicada).
 * Endpoint principal: `GET /api/paypal/status/{token}`.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Copy, Mail, ExternalLink, CheckCircle2, Clock, AlertCircle, X,
  RefreshCw, ShieldCheck, Loader2, RotateCcw, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CotPaymentSettings } from "./CotPaymentSettings";
import { CotExtras, CotRefundRequests, CotPayActivity } from "./CotPostVenta";

const eur = (n, cur = "EUR") => new Intl.NumberFormat("es-ES", { style: "currency", currency: cur }).format(n || 0);

const KIND_LABEL = {
  deposit: "Depósito 30%",
  balance: "Saldo restante 70%",
  full: "Pago total",
  custom: "Importe libre",
  complete_deposit: "Completar depósito",
};

const STATUS_BADGE = {
  CREATED: { text: "Enviado", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
  APPROVED: { text: "Aprobado", cls: "bg-blue-50 text-blue-800 border-blue-200", Icon: Clock },
  COMPLETED: { text: "Pagado", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  DENIED: { text: "Rechazado", cls: "bg-red-50 text-red-800 border-red-200", Icon: AlertCircle },
  REFUNDED: { text: "Reembolsado", cls: "bg-orange-50 text-orange-800 border-orange-200", Icon: RotateCcw },
};

const ADMIN_ROLES = new Set(["dir_admin_general", "socio_director", "admin", "administracion", "contable"]);

function copy(text, label = "Enlace") {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copiado`),
    () => toast.error("No se pudo copiar")
  );
}

export function CotPaymentsModal({ cotId, publicToken, clientEmail, onClose }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canRefund = true; // cualquier agente con acceso solicita; Dirección aprueba
  const [refundOpen, setRefundOpen] = useState(null); // pago seleccionado
  const [refundAmt, setRefundAmt] = useState("");
  const [refundNote, setRefundNote] = useState("");

  const statusQ = useQuery({
    queryKey: ["paypal-status", publicToken],
    queryFn: async () => (await api.get(`/paypal/status/${publicToken}`)).data,
    enabled: !!publicToken,
    refetchInterval: 15000,
  });

  const sendBalanceMut = useMutation({
    mutationFn: async () => (await api.post(`/paypal/balance/send/${publicToken}`)).data,
    onSuccess: () => toast.success("Email con el saldo restante enviado al cliente"),
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const refundMut = useMutation({
    mutationFn: async ({ orderId, amount, note }) => (await api.post("/paypal/refund-requests",
      { order_id: orderId, amount: amount ? Number(amount) : null, reason: note || "" })).data,
    onSuccess: () => {
      toast.success("Solicitud de devolución enviada a Dirección para su aprobación");
      setRefundOpen(null); setRefundAmt(""); setRefundNote("");
      qc.invalidateQueries({ queryKey: ["refund-requests", publicToken] });
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo crear la solicitud de devolución")),
  });

  const data = statusQ.data;
  const payUrl = data?.public_url;
  const currency = data?.currency || "EUR";
  const chargeCur = data?.charge_currency || "EUR";

  const emailMailto = () => {
    if (!payUrl) return;
    const subj = encodeURIComponent(`Link de pago · ${data?.title || "tu viaje"}`);
    const body = encodeURIComponent(
      `Hola ${data?.client_name || ""},\n\n` +
      `Aquí tienes el enlace para gestionar el pago de tu viaje:\n${payUrl}\n\n` +
      `Podrás pagar el depósito (30%), el saldo restante o el total.\n\n` +
      `Un saludo,\nEl equipo de Viaja de Verdad`
    );
    window.location.href = `mailto:${clientEmail || ""}?subject=${subj}&body=${body}`;
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="cot-payments-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />Cobros de la cotización
          </DialogTitle>
          <DialogDescription className="text-xs">
            Comparte el link con tu cliente para que pague con PayPal (30% depósito · total · saldo · parcial · reparto por viajero).
            Los movimientos se ven aquí en tiempo real.
          </DialogDescription>
        </DialogHeader>

        {statusQ.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !data ? (
          <p className="text-sm text-muted-foreground">No se pudo cargar el estado del pago.</p>
        ) : (
          <div className="space-y-4">
            {/* Link público */}
            <div className="space-y-1.5 rounded-lg border bg-emerald-50/50 p-3">
              <Label className="text-xs">🔗 Enlace de pago del cliente</Label>
              <div className="flex gap-1.5">
                <Input value={payUrl} readOnly className="font-mono text-xs" data-testid="cot-payments-link" />
                <Button variant="outline" size="sm" onClick={() => copy(payUrl, "Enlace de pago")} data-testid="cot-payments-copy">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" onClick={emailMailto} title="Enviar por email" data-testid="cot-payments-email">
                  <Mail className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open(payUrl, "_blank")} title="Abrir link" data-testid="cot-payments-open">
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">El cliente elige qué pagar (depósito, saldo o total).</p>
            </div>

            {/* KPIs saldo */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border p-2 text-center">
                <p className="text-[10px] uppercase text-muted-foreground">Total</p>
                <p className="text-lg font-bold tabular-nums" data-testid="cot-payments-total">{eur(data.total, currency)}</p>
                {chargeCur !== "EUR" && <p className="text-[10px] text-muted-foreground">{eur(data.total_charge, chargeCur)}</p>}
              </div>
              <div className="rounded-lg border p-2 text-center bg-emerald-50">
                <p className="text-[10px] uppercase text-emerald-700">Pagado</p>
                <p className="text-lg font-bold tabular-nums text-emerald-700" data-testid="cot-payments-paid">{eur(data.paid, currency)}</p>
                {chargeCur !== "EUR" && <p className="text-[10px] text-emerald-700/70">{eur(data.paid_charge, chargeCur)}</p>}
              </div>
              <div className="rounded-lg border p-2 text-center bg-amber-50">
                <p className="text-[10px] uppercase text-amber-700">Pendiente</p>
                <p className="text-lg font-bold tabular-nums text-amber-700" data-testid="cot-payments-pending">{eur(data.pending, currency)}</p>
                {chargeCur !== "EUR" && <p className="text-[10px] text-amber-700/70">{eur(data.pending_charge, chargeCur)}</p>}
              </div>
            </div>

            <CotPayActivity cotId={cotId} />
            <CotPaymentSettings cotId={cotId} publicToken={publicToken} />
            <CotExtras cotId={cotId} />
            <CotRefundRequests publicToken={publicToken} />

            {/* Acciones rápidas */}
            {data.paid > 0 && data.pending > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 text-amber-700 shrink-0" />
                <div className="flex-1 text-xs">
                  <p className="font-medium">Enviar recordatorio del saldo pendiente al cliente</p>
                  <p className="text-muted-foreground">Se enviará un email con el link + resumen del saldo restante.</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => sendBalanceMut.mutate()} disabled={sendBalanceMut.isPending} data-testid="cot-payments-remind">
                  {sendBalanceMut.isPending ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Enviando…</> : "Enviar recordatorio"}
                </Button>
              </div>
            )}

            {/* Historial */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Historial de pagos</Label>
                <Button variant="ghost" size="sm" onClick={() => statusQ.refetch()} className="h-6 px-2 text-[10px]" data-testid="cot-payments-refresh">
                  <RefreshCw className={`mr-1 h-3 w-3 ${statusQ.isFetching ? "animate-spin" : ""}`} />Actualizar
                </Button>
              </div>
              {(data.payments || []).length === 0 ? (
                <p className="rounded border border-dashed py-6 text-center text-xs text-muted-foreground" data-testid="cot-payments-empty">
                  Aún no hay pagos. En cuanto el cliente pague, aparecerán aquí.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {data.payments.map((p) => {
                    const B = STATUS_BADGE[p.status] || STATUS_BADGE.CREATED;
                    const BIcon = B.Icon;
                    return (
                      <div key={p.order_id} className="flex items-center gap-2 rounded-lg border bg-card p-2.5" data-testid={`cot-payment-row-${p.order_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium">{KIND_LABEL[p.kind] || p.kind}{p.payer_share_name ? ` · ${p.payer_share_name}` : ""}</span>
                            <Badge variant="outline" className={`text-[9px] ${B.cls}`}><BIcon className="mr-1 h-2.5 w-2.5" />{B.text}</Badge>
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            {p.payer_email || "sin email"} · {p.captured_at ? new Date(p.captured_at).toLocaleString("es-ES") : (p.created_at ? new Date(p.created_at).toLocaleString("es-ES") : "—")}
                            {p.tos_accepted_at && <span title={`T&C aceptadas ${new Date(p.tos_accepted_at).toLocaleString("es-ES")} · IP ${p.tos_ip || "—"}`}> · T&C ✓</span>}
                          </p>
                          {p.refunded_at && (
                            <p className="text-[10px] text-orange-700">
                              ⤺ Devuelto {p.refunded_amount ? eur(p.refunded_amount, currency) : ""} el {new Date(p.refunded_at).toLocaleDateString("es-ES")}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold tabular-nums">{eur(p.amount, p.currency || currency)}</span>
                          {(p.currency || "EUR") !== "EUR" && <p className="text-[10px] text-muted-foreground">{eur(p.amount_eur, "EUR")}</p>}
                        </div>
                        <div className="flex gap-0.5">
                          {p.receipt_file_id && (
                            <a href={`${api.defaults.baseURL}/paypal/receipt/${p.order_id}`} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Recibo PDF" data-testid={`cot-payment-receipt-${p.order_id}`}>
                                <FileText className="h-3.5 w-3.5" />
                              </Button>
                            </a>
                          )}
                          {canRefund && ["COMPLETED", "PARTIALLY_REFUNDED"].includes(p.status) && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-orange-700 hover:bg-orange-50"
                              onClick={() => { setRefundOpen(p); setRefundAmt(String(p.amount || "")); setRefundNote(""); }}
                              title="Solicitar devolución" data-testid={`cot-payment-refund-${p.order_id}`}>
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>

      {refundOpen && (
        <Dialog open onOpenChange={(o) => !o && setRefundOpen(null)}>
          <DialogContent className="max-w-md" data-testid="cot-refund-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-700"><RotateCcw className="h-5 w-5" />Devolver pago</DialogTitle>
              <DialogDescription className="text-xs">
                Se crea una <b>solicitud</b> que Dirección debe aprobar antes de que PayPal devuelva el dinero (en la moneda del cobro). Máximo devolvible: {eur(refundOpen.amount, refundOpen.currency || currency)}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2.5">
              <div>
                <Label className="text-xs">Importe a devolver ({refundOpen.currency || "EUR"})</Label>
                <Input type="number" step="0.01" value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} data-testid="cot-refund-amount" />
                <p className="mt-0.5 text-[10px] text-muted-foreground">Deja vacío para devolución total.</p>
              </div>
              <div>
                <Label className="text-xs">Motivo de la devolución</Label>
                <Textarea rows={2} value={refundNote} onChange={(e) => setRefundNote(e.target.value)} data-testid="cot-refund-note" placeholder="Ej.: devolución parcial acordada" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRefundOpen(null)}>Cancelar</Button>
              <Button className="bg-orange-600 hover:bg-orange-700" data-testid="cot-refund-confirm"
                onClick={() => refundMut.mutate({ orderId: refundOpen.order_id, amount: refundAmt || null, note: refundNote || null })}
                disabled={refundMut.isPending}>
                {refundMut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Enviando…</> : "Solicitar devolución"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

export default CotPaymentsModal;
